import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  useAreaNameByIdMap,
  useDragRidesPassengers,
  useModalConfirm,
} from "@ryde/redux";
import {
  ConfirmResult,
  DirectionEnum,
  errors,
  getPassengerMoveScopeTranslationKey,
  getPassengerScopeRestrictions,
  IDropdownItem,
  IRideDto,
  isSoftValidationSingleErrorResponse,
  Log,
  mergePassengerScopeRestrictions,
  RIDE_DROP_MAX_TIME_DIFFERENCE_MINUTES,
  rideSelectedPassengersIdsAF,
  ridesSelector,
  useFormats,
  useHasPermissions,
  useProfileAuthorizationCheck,
  useSystemParams,
  warnAndToastSimpleResultWithFallback,
} from "@ryde/shared";

import {
  IRideBulkMoveFormModel,
  RideBulkMoveTargetEnum,
} from "./ride-station-bulk-actions.models";
import {
  getRideBulkMoveSelectedPassengers,
  getRideBulkMoveTargetOptions,
  isRideBulkMoveTimeGapExceeded,
} from "./ride-station-bulk-move.utils";

export const useRideStationBulkMove = (rideDto: Partial<IRideDto>) => {
  const { t } = useTranslation();
  const formMethods = useForm<IRideBulkMoveFormModel>({
    defaultValues: {
      moveTarget: RideBulkMoveTargetEnum.NewRide,
      targetRideId: undefined,
    },
  });
  const { getValues, setValue, watch } = formMethods;
  const { formatTimeNormalized } = useFormats();
  const { dragTimeRangeMinutes } = useSystemParams();
  const { confirmDialogResult } = useModalConfirm();
  const { dropPassengersToRide } = useDragRidesPassengers();
  const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
  const { hasPermissions: hasCreateOtherSitePermission } = useHasPermissions({
    entity: "ride",
    action: "create_other_site",
  });
  const rides = useRecoilValue(ridesSelector);
  const selectedPassengersIds = useRecoilValue(
    rideSelectedPassengersIdsAF(rideDto?.id || 0),
  );
  const areaLabelsById = useAreaNameByIdMap();
  const isMoveInFlightRef = useRef(false);

  const moveTarget = watch("moveTarget");
  const targetRideId = watch("targetRideId");
  const isExistingRideTarget =
    moveTarget === RideBulkMoveTargetEnum.ExistingRide;

  const directionLabelTranslated = useMemo(() => {
    switch (rideDto?.direction) {
      case DirectionEnum.PickUp:
        return t("ride.BULK_MOVE_DIRECTION_A");
      case DirectionEnum.DropOff:
        return t("ride.BULK_MOVE_DIRECTION_B");
      default:
        return rideDto?.direction ?? "";
    }
  }, [rideDto?.direction, t]);

  const moveTargetDropdownList: IDropdownItem[] = useMemo(() => {
    return [
      {
        value: RideBulkMoveTargetEnum.NewRide,
        label: t("ride.BULK_MOVE_CREATE_RIDE_OPTION"),
      },
      {
        value: RideBulkMoveTargetEnum.ExistingRide,
        label: t("ride.BULK_MOVE_EXISTING_RIDE_OPTION"),
      },
    ];
  }, [t]);

  const targetRidesDropdownList: IDropdownItem[] = useMemo(() => {
    return getRideBulkMoveTargetOptions({
      rides,
      originRide: rideDto,
      areaLabelsById,
      formatAnchorTime: formatTimeNormalized,
    });
  }, [areaLabelsById, formatTimeNormalized, rideDto, rides]);

  /** Nothing in the grid can receive these passengers — don't offer a dead-end option. */
  const moveTargetDisabledValues = useMemo(() => {
    return targetRidesDropdownList.length
      ? undefined
      : [RideBulkMoveTargetEnum.ExistingRide];
  }, [targetRidesDropdownList.length]);

  useEffect(() => {
    if (!isExistingRideTarget && targetRideId) {
      setValue("targetRideId", undefined);
    }
  }, [isExistingRideTarget, setValue, targetRideId]);

  const movePassengersHandler = useEvent(async (): Promise<void> => {
    // The confirm dialogs leave gaps where the button is clickable again, and a second
    // move would duplicate a destructive action.
    if (isMoveInFlightRef.current) {
      return;
    }

    isMoveInFlightRef.current = true;

    try {
      const passengers = getRideBulkMoveSelectedPassengers({
        ride: rideDto,
        selectedPassengersIds,
      });

      const passengerScopeTranslationKey = passengers.length
        ? getPassengerMoveScopeTranslationKey({
            restrictions: mergePassengerScopeRestrictions(
              passengers.map(passenger =>
                getPassengerScopeRestrictions(
                  passenger,
                  profileAuthorizationInCheck,
                ),
              ),
            ),
            hasCreateOtherSite: hasCreateOtherSitePermission,
          })
        : undefined;

      if (passengerScopeTranslationKey) {
        Log.warnAndToast(t(passengerScopeTranslationKey));
        return;
      }

      const isExistingRideSelected =
        getValues("moveTarget") === RideBulkMoveTargetEnum.ExistingRide;
      const selectedTargetRideId = getValues("targetRideId");
      const targetRide = isExistingRideSelected
        ? rides.find(ride => ride.id === Number(selectedTargetRideId))
        : undefined;

      if (isExistingRideSelected && !targetRide) {
        Log.warnAndToast(t("ride.BULK_MOVE_TARGET_RIDE_REQUIRED"));
        return;
      }

      const maxGapMinutes =
        dragTimeRangeMinutes || RIDE_DROP_MAX_TIME_DIFFERENCE_MINUTES;

      if (
        targetRide &&
        isRideBulkMoveTimeGapExceeded({
          originRide: rideDto,
          targetRide,
          maxGapMinutes,
        })
      ) {
        const timeGapConfirmResult = await confirmDialogResult({
          title: t("general.ARE_YOU_SURE"),
          message: t("ride.BULK_MOVE_TIME_GAP_CONFIRM_MESSAGE", {
            maxMinutes: maxGapMinutes,
          }),
        });

        if (timeGapConfirmResult !== ConfirmResult.OK) {
          return;
        }
      }

      const moveResult = await dropPassengersToRide(
        passengers,
        rideDto,
        targetRide,
      );

      if (moveResult.success) {
        Log.debug("Bulk move passengers performed successfully", passengers);
        return;
      }

      if (!isSoftValidationSingleErrorResponse(moveResult.data)) {
        warnAndToastSimpleResultWithFallback(
          moveResult,
          t,
          errors.ride.rideMovePassengersError,
        );
      }
    } finally {
      isMoveInFlightRef.current = false;
    }
  });

  return {
    formMethods,
    directionLabelTranslated,
    moveTargetDropdownList,
    moveTargetDisabledValues,
    targetRidesDropdownList,
    isExistingRideTarget,
    movePassengersHandler,
  };
};
